export const VERSION_JSON_PATH = '/version.json';
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
export const CHUNK_RELOAD_STORAGE_KEY = 'cfsf_chunk_reload_at';
export const CHUNK_RELOAD_COOLDOWN_MS = 15000;
export const HARD_RELOAD_QUERY_PARAM = '_reload';

export function getCurrentBuildId() {
  return String(process.env.REACT_APP_BUILD_ID || '').trim();
}

export function isVersionCheckEnabled(buildId = getCurrentBuildId()) {
  return Boolean(buildId);
}

export function isStaleBuild({ currentBuildId, remoteBuildId }) {
  const current = String(currentBuildId || '').trim();
  const remote = String(remoteBuildId || '').trim();
  if (!current || !remote) {
    return false;
  }
  return current !== remote;
}

export async function fetchRemoteBuildId({
  fetchImpl = fetch,
  now = Date.now()
} = {}) {
  const response = await fetchImpl(`${VERSION_JSON_PATH}?t=${now}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) {
    return { ok: false, buildId: null };
  }
  const data = await response.json();
  const buildId = typeof data?.buildId === 'string' ? data.buildId.trim() : '';
  if (!buildId) {
    return { ok: false, buildId: null };
  }
  return { ok: true, buildId };
}

export async function checkForStaleAppVersion(options = {}) {
  const currentBuildId = options.currentBuildId ?? getCurrentBuildId();
  if (!isVersionCheckEnabled(currentBuildId)) {
    return { stale: false, skipped: true };
  }
  try {
    const remote = await fetchRemoteBuildId(options);
    if (!remote.ok) {
      return { stale: false, skipped: false, error: true };
    }
    return {
      stale: isStaleBuild({
        currentBuildId,
        remoteBuildId: remote.buildId
      }),
      skipped: false,
      currentBuildId,
      remoteBuildId: remote.buildId
    };
  } catch {
    return { stale: false, skipped: false, error: true };
  }
}

export function buildHardRefreshUrl(href, now = Date.now()) {
  const url = new URL(href);
  url.searchParams.set(HARD_RELOAD_QUERY_PARAM, String(now));
  return url.toString();
}

export async function clearClientAssetCaches({
  serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  cacheStorage = typeof caches !== 'undefined' ? caches : null
} = {}) {
  if (serviceWorker?.getRegistrations) {
    const registrations = await serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if (cacheStorage?.keys) {
    const keys = await cacheStorage.keys();
    await Promise.all(keys.map((key) => cacheStorage.delete(key)));
  }
}

export async function hardRefreshApp({
  href,
  now = Date.now(),
  replace = (nextHref) => {
    window.location.replace(nextHref);
  },
  serviceWorker,
  cacheStorage
} = {}) {
  try {
    await clearClientAssetCaches({ serviceWorker, cacheStorage });
  } catch {
    // Still navigate so the user can pick up the new HTML/JS.
  }
  const currentHref = href || (typeof window !== 'undefined' ? window.location.href : '');
  replace(buildHardRefreshUrl(currentHref, now));
}

export function shouldSkipRecentChunkReload({
  now = Date.now(),
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
} = {}) {
  if (!storage) {
    return false;
  }
  const last = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
  if (last && now - parseInt(last, 10) < CHUNK_RELOAD_COOLDOWN_MS) {
    return true;
  }
  storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  return false;
}
